import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Calendar, Clock, Users, MapPin, Lock, Globe, CalendarPlus, Flag, MoreHorizontal, Target } from 'lucide-react';
import ReportModal from './ReportModal';
import { useNavigate } from 'react-router-dom';
import { toast } from './notifications';
import { SESSION_TYPE, SESSION_STATUS, PARTICIPANT_STATUS } from '../constants';
import { findActiveParticipant, findParticipant, countActiveParticipants, isActiveParticipant } from '../utils/participantUtils';
import Badge from './ui/Badge.jsx';
import Button from './ui/Button';
import Card from './ui/Card.jsx';
import Input from './ui/Input.jsx';
import AvatarStack from './ui/AvatarStack.jsx';
import AnimatedNumber from './ui/AnimatedNumber.jsx';
import ActionSheet from './ui/ActionSheet.jsx';

// Status rechts im Fuss der Karte: ruhiges Wort mit Punkt statt farbiger Schrift.
const StatusWord = ({ dot, children }) => (
  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-2">
    <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />
    {children}
  </span>
);

StatusWord.propTypes = { dot: PropTypes.string.isRequired, children: PropTypes.node };

const SessionCard = ({
  session,
  onJoinSession,
  currentUser,
  onValidateCode
}) => {
  const navigate = useNavigate();
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);

  // Schema-kompatible Zugriffe
  // Robuster ID-Vergleich: String-Konvertierung fuer sicheren Vergleich (Backend kann String oder Number liefern)
  const currentId = currentUser?.id ? String(currentUser.id) : null;
  const isHost = currentId && String(session.hostId) === currentId;
  const participants = session.participants || [];
  const maxParticipants = session.maxParticipants || 2;
  // Nur aktive Teilnehmer belegen Plaetze - LEFT/KICKED wuerden sonst Kapazitaet blockieren
  const hasCapacity = countActiveParticipants(participants) < maxParticipants;

  // Prüfe aktuellen Teilnehmer-Status (nur aktive Teilnahme, LEFT zaehlt nicht)
  const currentParticipant = findActiveParticipant(participants, currentId) || null;
  const userStatus = currentParticipant?.status || null;
  const isParticipant = !!currentParticipant;

  // Wer dabei ist, aber noch nicht validiert, braucht die Code-Eingabe - unabhaengig vom
  // konkreten Status. Frueher wurde nur PENDING geprueft; ein vom Host eingeladener
  // Lernpartner (RESERVED) sah deshalb faelschlich "Du nimmst teil" ohne jeden Weg hinein.
  const awaitsValidation = isParticipant && userStatus !== PARTICIPANT_STATUS.VALIDATED;

  // Der eigene Datensatz wird auch dann geliefert, wenn er KICKED/REJECTED ist
  // (GET /sessions filtert Entfernte nur fuer Fremde heraus). Ohne diese Pruefung
  // sieht ein Entfernter hier einen scharfen Beitreten-Knopf, der serverseitig
  // garantiert scheitert - findActiveParticipant blendet ihn ja aus.
  const ownRecord = findParticipant(participants, currentId);
  const ownStatus = ownRecord?.status ? String(ownRecord.status).toUpperCase() : null;
  const wasRemoved = !isHost
    && (ownStatus === PARTICIPANT_STATUS.KICKED || ownStatus === PARTICIPANT_STATUS.REJECTED);
  
  // Handle different date/time formats
  const sessionTime = session.startTime ? 
    new Date(session.startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) :
    session.time;
    
  // „Do., 08.10.“ wie im Spot — der Wochentag hilft mehr als das Jahr.
  const dateSource = session.startTime || session.date;
  const sessionDate = dateSource
    ? new Date(dateSource).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })
    : null;

  const handleJoin = async () => {
    if (session.type === SESSION_TYPE.OPEN) {
      try {
        // Bei offenen Sessions direkt beitreten - auf das Ergebnis warten!
        // Ohne await feuerte die Bestaetigung auch, wenn das Backend ablehnte
        // (z.B. gekickter Teilnehmer, volle Session).
        // Kein Erfolgs-Toast: Der Join-Flow leitet auf die Session-Seite weiter,
        // dort stehen Overlay und Status-Badge ("Reserviert").
        await onJoinSession(session.id);
      } catch {
        // Fehler-Toast kommt bereits aus dem Join-Flow
      }
    } else {
      // Bei privaten Sessions Code eingeben
      setShowCodeInput(true);
    }
  };

  // Fehler abfangen: onJoinSession lehnt seit August 2026 ab (statt still zu
  // schlucken) und zeigt den Toast selbst - ohne catch bliebe hier eine
  // unbehandelte Promise-Rejection.
  const handleGoToSession = async () => {
    try {
      await onJoinSession(session.id);
    } catch {
      // Toast kommt bereits aus dem Join-Flow
    }
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) {
      setError('Bitte Code eingeben');
      return;
    }
    try {
      if (onValidateCode) {
        await onValidateCode(session.id, code);
      } else {
        // Fallback: direkt onJoinSession aufrufen (await, damit der
        // Fehlerfall unten landet statt in einer Erfolgs-Bestaetigung)
        await onJoinSession(session.id, code);
      }
      setShowCodeInput(false);
      setCode('');
      setError('');
    } catch (error) {
      setError(error.message || 'Fehler beim Beitreten');
    }
  };

  // Type-Badge: Icon + Label, dezent im Design-System-Stil
  const getTypeBadge = () => {
    switch (session.type) {
      case SESSION_TYPE.OPEN:
        return { Icon: Globe, label: 'Offen', tone: 'success' };
      case SESSION_TYPE.GROUP:
        return { Icon: Users, label: 'Gruppe', tone: 'neutral' };
      case SESSION_TYPE.PRIVATE:
      default:
        return { Icon: Lock, label: 'Privat', tone: 'neutral' };
    }
  };

  // Status-Badge: deutsches Label + Farbe
  const getStatusBadge = () => {
    switch (session.status) {
      case SESSION_STATUS.ACTIVE:
        return { label: 'Aktiv', tone: 'success' };
      case SESSION_STATUS.PLANNED:
        return { label: 'Geplant', tone: 'info' };
      case SESSION_STATUS.COMPLETED:
        return { label: 'Beendet', tone: 'neutral' };
      default:
        return { label: session.status, tone: 'neutral' };
    }
  };

  const handleExportIcal = () => {
    const start = session.startTime ? new Date(session.startTime) : new Date();
    // Rueckfall wie der DB-Default von tenant_sessions.maxDuration (90 Minuten)
    const durationMin = session.maxDuration || 90;
    const end = new Date(start.getTime() + durationMin * 60 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const now = new Date();
    const ical = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EachOtter//Sessions//DE',
      'BEGIN:VEVENT',
      `UID:${session.id}@eachotter.de`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `DTSTAMP:${fmt(now)}`,
      `SUMMARY:${(session.topic || 'Session').replace(/[,;\\]/g, ' ')}`,
      `DESCRIPTION:${(session.description || '').replace(/[,;\\]/g, ' ').replace(/\n/g, '\\n')}`,
      `LOCATION:${(session.location || '').replace(/[,;\\]/g, ' ')}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ical], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-${session.topic?.replace(/\s+/g, '-').substring(0, 30) || session.id}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Kalender-Eintrag heruntergeladen');
  };

  const typeBadge = getTypeBadge();
  const statusBadge = getStatusBadge();
  const TypeIcon = typeBadge.Icon;
  // Rot nur, wenn „voll“ fuer mich etwas bedeutet: Ich will hinein und kann nicht.
  // Eine private Session zu zweit ist mit 2/2 nicht voll, sondern vollstaendig.
  const capacityFull = !hasCapacity && !isHost && !isParticipant;
  // Belegung zaehlt nur aktive Teilnahmen (wie hasCapacity). Vorher stand hier
  // participants.length — Gegangene und Entfernte belegten scheinbar Plaetze.
  const activeCount = countActiveParticipants(participants);

  // Avatar-Stapel wie im Spot: aktive Teilnehmende, Host zuerst.
  const people = participants
    .filter(isActiveParticipant)
    .sort((a, b) => Number(Boolean(b.isHost) || String(b.userId ?? b.id) === String(session.hostId))
      - Number(Boolean(a.isHost) || String(a.userId ?? a.id) === String(session.hostId)))
    .map(p => ({ id: p.userId ?? p.id, name: p.name, src: p.image || p.avatar || undefined }));

  const goals = (session.learningGoals || []).slice(0, 2);

  // Selten gebraucht, deshalb hinter „…“ statt als Icon-Leiste im Fuss.
  const actions = [
    {
      key: 'calendar',
      icon: CalendarPlus,
      label: 'Zum Kalender hinzufügen',
      hidden: !(session.status === SESSION_STATUS.PLANNED && session.startTime),
      onSelect: handleExportIcal,
    },
    {
      key: 'report',
      icon: Flag,
      label: 'Session melden',
      tone: 'danger',
      hidden: isHost,
      onSelect: () => setShowReportModal(true),
    },
  ];
  const hasActions = actions.some(a => !a.hidden);

  // Die ganze Karte fuehrt zur Detailseite - der fruehere "Details"-Button kostete
  // eine komplette Zeile. Aktions-Buttons stoppen die Weitergabe des Klicks (siehe unten).
  const openDetails = () => navigate(`/session/${session.id}`);

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetails();
    }
  };

  const stop = (e) => e.stopPropagation();

  return (
    // ui/Card mit onClick: Rolle, Tastaturbedienung und Fokus-Ring kommen aus dem
    // Baustein. Okt 2026: aufgebaut wie die Session-Karte im Werbespot — Badges,
    // Titel, Fakten, Ziele, unten Avatar-Stapel und Knopf. Kein Glas mehr.
    <Card
      onClick={openDetails}
      onKeyDown={handleCardKeyDown}
      aria-label={`Details zur Session "${session.topic}" anzeigen`}
      className="group overflow-hidden"
    >
      <div className="px-4 pb-4 pt-4 sm:px-[18px] sm:pt-[18px]">
        {/* Badges: Status links, Typ und Belegung rechts */}
        <div className="flex items-center gap-1.5">
          <Badge size="lg" tone={statusBadge.tone}>{statusBadge.label}</Badge>
          <span className="flex-1" />
          <Badge size="lg" tone={typeBadge.tone} icon={TypeIcon}>{typeBadge.label}</Badge>
          <Badge size="lg" tone={capacityFull ? 'danger' : 'neutral'} icon={Users}>
            <span className="tabular-nums">
              <AnimatedNumber value={activeCount} />/{maxParticipants === 999 ? '∞' : maxParticipants}
            </span>
          </Badge>
          {hasActions && (
            <button
              type="button"
              onClick={(e) => { stop(e); setShowActions(true); }}
              onKeyDown={stop}
              aria-label="Weitere Optionen"
              title="Weitere Optionen"
              className="-my-1 -mr-2.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-faint transition-colors duration-fast hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </div>

        <h4 className="mb-1.5 mt-3 text-title-m font-bold text-foreground">{session.topic}</h4>

        {session.description && (
          <p className="mb-3 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{session.description}</p>
        )}

        {/* Ort, Zeit & Datum — Text eine Stufe kraeftiger als die Icons */}
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-sm text-foreground-2">
          {session.location && (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <MapPin className="h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
              <span className="truncate">{session.location}</span>
            </span>
          )}
          {sessionTime && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
              {sessionTime}
            </span>
          )}
          {sessionDate && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
              {sessionDate}
            </span>
          )}
        </div>

        {/* Lernziele (nur fuer Host und aktive Teilnehmende geliefert) */}
        {goals.length > 0 && (
          <ul className="mt-3 space-y-1.5" aria-label="Lernziele">
            {goals.map(goal => (
              <li key={goal} className="flex items-center gap-2 text-label text-foreground-2">
                <Target className="h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
                <span className="truncate">{goal}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Fuss: Avatar-Stapel + Knopf.
            stopPropagation: Die Karte selbst oeffnet die Detailseite. Ein Klick auf
            "Beitreten" oder ins Code-Feld soll NUR diese Aktion ausloesen. */}
        <div onClick={stop} onKeyDown={stop} className="mt-3.5 border-t border-border pt-3.5">
        {showCodeInput ? (
          <div className="space-y-2">
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Session-Code eingeben"
              aria-label="Session-Code"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              className="font-mono tracking-wider"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitCode()}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSubmitCode} className="flex-1">
                Beitreten
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowCodeInput(false);
                  setCode('');
                  setError('');
                }}
              >
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-[40px] items-center justify-between gap-3">
            <AvatarStack people={people} size="sm" max={4} />

            {/* Status-/Aktions-Bereich rechts */}
            {isHost ? (
              <StatusWord dot="bg-faint">Du bist Host</StatusWord>
            ) : awaitsValidation ? (
              <Button size="sm" onClick={(e) => { e.stopPropagation(); setShowCodeInput(true); }}>
                Session-Code eingeben
              </Button>
            ) : userStatus === PARTICIPANT_STATUS.VALIDATED ? (
              session.status === SESSION_STATUS.ACTIVE ? (
                <Button variant="success" size="sm" onClick={handleGoToSession}>
                  Zur Session
                </Button>
              ) : (
                <StatusWord dot="bg-success">Validiert · startet bald</StatusWord>
              )
            ) : isParticipant ? (
              <StatusWord dot="bg-success">Du nimmst teil</StatusWord>
            ) : wasRemoved ? (
              <span className="text-sm font-medium text-muted-foreground">Entfernt</span>
            ) : (
              <Button size="sm" onClick={handleJoin} disabled={!hasCapacity} className="ml-auto">
                {!hasCapacity ? 'Session voll' :
                 session.status === SESSION_STATUS.PLANNED ? 'Platz reservieren' :
                 session.type === SESSION_TYPE.OPEN ? 'Beitreten' : 'Code eingeben'}
              </Button>
            )}
          </div>
        )}
        </div>

        {error && (
          <div className="mt-3 rounded-xl border border-danger/40 bg-danger-soft p-3 text-sm text-foreground" role="alert">
            {error}
          </div>
        )}
      </div>

      {/* Aktionsliste liegt in der klickbaren Karte -> Klicks darin duerfen
          nicht zur Detailseite durchschlagen. */}
      <div onClick={stop} onKeyDown={stop}>
        <ActionSheet
          open={showActions}
          onClose={() => setShowActions(false)}
          title="Session"
          actions={actions}
        />
      </div>

      {/* Melde-Formular (Session melden)
          Liegt innerhalb der klickbaren Karte -> Klicks im Dialog duerfen nicht
          zur Detailseite durchschlagen. */}
      {showReportModal && (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <ReportModal
          targetType="SESSION"
          targetId={session.id}
          reportedUserId={String(session.hostId)}
          targetLabel={session.topic}
          onClose={() => setShowReportModal(false)}
        />
        </div>
      )}
    </Card>
  );
};

SessionCard.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    schoolId: PropTypes.string,
    topic: PropTypes.string.isRequired,
    description: PropTypes.string,
    location: PropTypes.string.isRequired,
    hostId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    maxParticipants: PropTypes.number,
    type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    verificationCode: PropTypes.string,
    // Legacy support
    time: PropTypes.string,
    date: PropTypes.string,
    participants: PropTypes.array.isRequired,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string
  }).isRequired,
  onJoinSession: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }),
  onValidateCode: PropTypes.func
};

// Memoisiert: Bei Suche/Tab-Wechsel auf der SessionsPage re-rendern nur Karten,
// deren session-Referenz sich tatsächlich geändert hat
export default React.memo(SessionCard);
